import styled from "@emotion/styled";
import arrayMutators from "final-form-arrays";
import * as React from "react";
import { Field, Form, useForm } from "react-final-form";
import { FieldArray, FieldArrayRenderProps } from "react-final-form-arrays";

import { Close } from "@livechat/design-system-icons";
import {
  AutoComplete,
  Button,
  FormField,
  Icon,
  Modal,
  ModalPortal,
  SpacingToken,
} from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { isEmail } from "helpers/validate";
import { useDebouncedAddresses } from "hooks/useDebounceGetAddresses";
import { IRequester } from "interfaces/entities/ticket";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";

interface IProps {
  emails: string[];
  requester: IRequester;
  onClose(): void;
  onSubmit(emails: string[]): void;
  agentsEmails: string[];
}

export const ChangeTicketCcsModal: React.FC<IProps> = ({ emails, requester, agentsEmails, onClose, onSubmit }) => {
  const handleSubmit = (values: { emails: string[] }): void => {
    onSubmit(values.emails.filter(email => email && email.trim().length > 0));
  };

  return (
    <ModalPortal zIndex={StackingContextLevel.Modal}>
      <Form<{ emails: string[] }>
        onSubmit={handleSubmit}
        mutators={{ ...arrayMutators }}
        initialValues={{ emails: emails.length > 0 ? emails : [""] }}
        keepDirtyOnReinitialize
        render={({ handleSubmit, valid, values }) => (
          <form onSubmit={handleSubmit}>
            <Modal
              heading="People in the loop"
              onClose={onClose}
              footer={
                <Flex gap={withVar(SpacingToken.Spacing2)}>
                  <Button onClick={onClose} kind="secondary">
                    Cancel
                  </Button>
                  <Button kind="primary" disabled={!valid} type="submit">
                    Save
                  </Button>
                </Flex>
              }
            >
              <FieldArray<string, HTMLElement> name="emails">
                {({ fields }) => (
                  <Fields>
                    {fields.map((name, index) => (
                      <EmailField
                        key={name}
                        index={index}
                        name={name}
                        requester={requester}
                        agentsEmails={agentsEmails}
                        values={values}
                        fields={fields}
                      />
                    ))}
                    <Button kind="link" onClick={() => fields.push("")}>
                      + Add another
                    </Button>
                  </Fields>
                )}
              </FieldArray>
            </Modal>
          </form>
        )}
      />
    </ModalPortal>
  );
};

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(${SpacingToken.Spacing2});
  align-items: flex-start;
`;

const EmailField: React.FC<{
  name: string;
  index: number;
  requester: IRequester;
  agentsEmails: string[];
  values: { emails: string[] };
  fields: FieldArrayRenderProps<string, HTMLElement>["fields"];
}> = ({ name, requester, agentsEmails, values, fields, index }) => {
  const form = useForm();

  return (
    <Field<string> name={name} validate={email => validateEmail(email, requester, agentsEmails)}>
      {({ input, meta }) => {
        const addresses = useDebouncedAddresses("cc", input.value);
        const filteredAddresses = addresses?.filter(
          address => !validateEmail(address, requester, agentsEmails) && !values.emails.includes(address)
        );
        const shouldShowError = meta.touched || (meta.pristine && input.value.length > 0);
        return (
          <StyledInputWrapper>
            <FormField
              error={shouldShowError && meta.error}
              css={{ marginBottom: 0, width: "320px", maxWidth: "100%" }}
            >
              <FieldWrapper>
                <AutoComplete
                  data-1p-ignore
                  autoFocus
                  options={filteredAddresses}
                  maxLength={1000}
                  placeholder="Email"
                  id={`email_${index}`}
                  error={shouldShowError && meta.error}
                  {...input}
                  onChange={e => {
                    input.onChange(e);
                    form.resetFieldState(name);
                  }}
                />
                {((fields.length ?? 0) > 1 || (input.value && input.value.length > 0)) && (
                  <Button
                    size="xcompact"
                    kind="plain"
                    icon={<Icon source={Close} />}
                    onClick={() => {
                      if (fields.length === 1) {
                        input.onChange("");
                      } else {
                        fields.remove(index);
                      }
                    }}
                  />
                )}
              </FieldWrapper>
            </FormField>
          </StyledInputWrapper>
        );
      }}
    </Field>
  );
};

const FieldWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(${SpacingToken.Spacing1});
  width: 320px;
  max-width: 100%;
  & > div {
    flex-grow: 1;
  }
`;

function isValidEmail(email: string): boolean {
  const value = email.trim();
  return value.length === 0 || isEmail(value);
}

function validateEmail(email: string | undefined, requester: IRequester, agentsEmails: string[]): string | undefined {
  if (!email) {
    return undefined;
  }
  if (!isValidEmail(email)) {
    return "Email is incorrect";
  }
  if (email === requester.email) {
    return "This person is already in the loop";
  }
  if (agentsEmails.some(agentEmail => agentEmail === email)) {
    return "Agents can only be added to Followers.";
  }
  return undefined;
}

const StyledInputWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(${SpacingToken.Spacing1});
  width: 320px;
  max-width: 100%;
`;
